import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Link,
    Grid,
    Divider,
    useTheme,
    alpha
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_THEME, type ThemeColors } from '../constants/themeDefaults';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LoginLayout from '../components/layout/LoginLayout';
import { LoginResponse } from '../types/auth';

type LoginMode = 'password' | 'otp-email' | 'otp-code';

// Define validation interface
interface ValidationState {
    email: string | null;
    password: string | null;
}

export const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [loginMode, setLoginMode] = useState<LoginMode>('password');
    const [apiErrors, setApiErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { login, loginWithOtp, sendOtp, isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const theme = DEFAULT_THEME;

    // Add validation state
    const [validationErrors, setValidationErrors] = useState<ValidationState>({
        email: null,
        password: null
    });

    // Add form submission tracking
    const [formSubmitted, setFormSubmitted] = useState(false);

    useEffect(() => {
        if (isAuthenticated && !isAuthLoading) {
            navigate('/dashboard');
        }
    }, [isAuthenticated, isAuthLoading, navigate]);

    if (isAuthLoading) {
        return null;
    }

    // Validate email
    const validateEmail = (value: string) => {
        if (!value.trim()) {
            return "Email address is required";
        }
        return null;
    };

    // Validate password
    const validatePassword = (value: string) => {
        if (!value.trim()) {
            return "Password is required";
        }
        return null;
    };

    // Add handlers for blur events
    const handleBlur = (field: keyof ValidationState) => {
        let errorMessage: string | null = null;

        switch(field) {
            case 'email':
                errorMessage = validateEmail(email);
                break;
            case 'password':
                errorMessage = validatePassword(password);
                break;
        }

        setValidationErrors(prev => ({
            ...prev,
            [field]: errorMessage
        }));
    };

    // Validate all fields
    const validateForm = () => {
        const newValidationState: ValidationState = {
            email: validateEmail(email),
            password: loginMode === 'password' ? validatePassword(password) : null
        };

        setValidationErrors(newValidationState);

        // Return true if no errors
        return !Object.values(newValidationState).some(error => error !== null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setApiErrors([]);
        setSuccessMessage('');
        setFormSubmitted(true);

        // Validate all fields before submission
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const result = await login(email, password) as LoginResponse;

            if (result.succeeded && result.token) {
                // Login successful - navigate to dashboard
                navigate('/dashboard');
            } else {
                // Check for API validation errors
                if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
                    // Set API errors for display
                    setApiErrors(result.errors);
                } else {
                    // Generic error if no specific errors provided
                    setApiErrors(['Login failed. Please check your credentials and try again.']);
                }
            }
        } catch (err: any) {
            console.error('Login error:', err);


            // Try to extract errors from the error response
            if (err.response && err.response.data) {
                const responseData = err.response.data;

                if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                    setApiErrors(responseData.errors);
                    return;
                }
            } else {
                setApiErrors(['An error occurred during login. Please try again.']);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setApiErrors([]);
        setSuccessMessage('');
        setFormSubmitted(true);

        const emailError = validateEmail(email);
        setValidationErrors(prev => ({ ...prev, email: emailError }));
        if (emailError) return;

        setIsLoading(true);
        try {
            const result = await sendOtp(email);
            if (result.success) {
                setLoginMode('otp-code');
                setFormSubmitted(false);
                setSuccessMessage(`Code sent to ${email}`);
            } else {
                setApiErrors([result.message || 'Failed to send code.']);
            }
        } catch (err: any) {
            console.error('Send OTP error:', err);
            setApiErrors(['An error occurred. Please try again.']);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setApiErrors([]);
        setSuccessMessage('');
        setFormSubmitted(true);

        if (!otpCode.trim()) {
            setApiErrors(['Please enter the 6-digit code.']);
            return;
        }
        if (!/^\d{6}$/.test(otpCode)) {
            setApiErrors(['Code must be exactly 6 digits.']);
            return;
        }

        setIsLoading(true);
        try {
            const result = await loginWithOtp(email, otpCode) as LoginResponse;
            if (result.succeeded && result.token) {
                navigate('/dashboard');
            } else {
                if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
                    setApiErrors(result.errors);
                } else {
                    setApiErrors(['Login failed. Please check your code and try again.']);
                }
            }
        } catch (err: any) {
            console.error('OTP login error:', err);
            if (err.response && err.response.data) {
                const responseData = err.response.data;
                if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                    setApiErrors(responseData.errors);
                    return;
                }
            }
            setApiErrors(['An error occurred during login. Please try again.']);
        } finally {
            setIsLoading(false);
        }
    };

    const switchToOtpMode = () => {
        setLoginMode('otp-email');
        setApiErrors([]);
        setSuccessMessage('');
        setFormSubmitted(false);
        setValidationErrors({ email: null, password: null });
        setOtpCode('');
    };

    const switchToPasswordMode = () => {
        setLoginMode('password');
        setApiErrors([]);
        setSuccessMessage('');
        setFormSubmitted(false);
        setValidationErrors({ email: null, password: null });
        setOtpCode('');
    };

    const handleResendCode = async () => {
        setApiErrors([]);
        setSuccessMessage('');
        setIsLoading(true);
        try {
            const result = await sendOtp(email);
            if (result.success) {
                setSuccessMessage('A new code has been sent.');
                setOtpCode('');
            } else {
                setApiErrors([result.message || 'Failed to resend code.']);
            }
        } catch {
            setApiErrors(['An error occurred. Please try again.']);
        } finally {
            setIsLoading(false);
        }
    };

    const textFieldSx = {
        mb: 3,
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '&.Mui-focused fieldset': {
                borderColor: theme.navigationColor,
            },
        },
    };

    const linkSx = {
        fontWeight: 600,
        color: theme.navigationColor,
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' }
    };

    const buttonSx = {
        py: 1.5,
        backgroundColor: theme.navigationColor,
        color: theme.secondaryColor,
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '1rem',
        '&:hover': {
            backgroundColor: alpha(theme.navigationColor, 0.9),
        },
    };

    const renderPasswordForm = () => (
        <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
                error={formSubmitted ? !!validationErrors.email : false}
                helperText={formSubmitted ? validationErrors.email : null}
                disabled={isLoading}
                sx={textFieldSx}
            />
            <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => handleBlur('password')}
                error={formSubmitted ? !!validationErrors.password : false}
                helperText={formSubmitted ? validationErrors.password : null}
                disabled={isLoading}
                sx={textFieldSx}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Link
                    component={RouterLink}
                    to="/forgot-password"
                    variant="body2"
                    sx={{
                        color: theme.navigationColor,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                    }}>
                    Forgot password?
                </Link>
            </Box>

            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={buttonSx}
            >
                {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
            </Button>

            <Box sx={{ my: 3, display: 'flex', alignItems: 'center' }}>
                <Divider sx={{ flex: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>or</Typography>
                <Divider sx={{ flex: 1 }} />
            </Box>

            <Box sx={{ textAlign: 'center' }}>
                <Button
                    type="button"
                    fullWidth
                    variant="outlined"
                    onClick={switchToOtpMode}
                    disabled={isLoading}
                >
                    {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Quick Login'}
                </Button>
            </Box>

        </Box>
    );

    const renderOtpEmailForm = () => (
        <Box component="form" onSubmit={handleSendOtp} noValidate>
            <TextField
                margin="normal"
                required
                fullWidth
                id="otp-email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
                error={formSubmitted ? !!validationErrors.email : false}
                helperText={formSubmitted ? validationErrors.email : null}
                disabled={isLoading}
                sx={textFieldSx}
            />

            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={buttonSx}
            >
                {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send Code'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={switchToPasswordMode}
                    sx={linkSx}
                >
                    Back to password login
                </Link>
            </Box>
        </Box>
    );

    const renderOtpCodeForm = () => (
        <Box component="form" onSubmit={handleOtpLogin} noValidate>
            <TextField
                margin="normal"
                required
                fullWidth
                id="otp-code"
                label="6-Digit Code"
                name="code"
                autoComplete="one-time-code"
                autoFocus
                value={otpCode}
                onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpCode(val);
                }}
                inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                disabled={isLoading}
                sx={textFieldSx}
            />

            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={buttonSx}
            >
                {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify & Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleResendCode}
                    sx={linkSx}
                >
                    Resend Code
                </Link>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={switchToPasswordMode}
                    sx={linkSx}
                >
                    Back to password login
                </Link>
            </Box>
        </Box>
    );

    const getSubtitle = () => {
        switch (loginMode) {
            case 'password':
                return 'Please enter your credentials to sign in';
            case 'otp-email':
                return 'Enter your email to receive a login code';
            case 'otp-code':
                return `Enter the code sent to ${email}`;
        }
    };

    return (
        <LoginLayout>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    bgcolor: theme.backgroundColor
                }}
            >
                <Grid container>
                    {/* Left side - Form */}
                    <Grid item xs={12} md={5} lg={4} component={Paper} elevation={6} square
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: { xs: 4, md: 8 },
                            zIndex: 1,
                            position: 'relative',
                            boxShadow: '0 0 40px rgba(0,0,0,0.1)',
                            backgroundColor: theme.backgroundColor,
                        }}
                    >
                        <Box sx={{ mb: 5, display: 'flex', alignItems: 'center' }}>
                            <CalendarMonthIcon sx={{ color: theme.navigationColor, mr: 1, fontSize: '2rem' }} />
                            <Typography variant="h4" fontWeight="700" color={theme.textColor}>
                                MindShare
                            </Typography>
                        </Box>

                        <Typography variant="h4" fontWeight="700" gutterBottom color={theme.textColor}>
                            Welcome back
                        </Typography>

                        <Typography variant="body1" color="text.secondary" mb={4}>
                            {getSubtitle()}
                        </Typography>

                        {/* Display API validation errors */}
                        {apiErrors.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Alert severity="error" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                                    {apiErrors.map((error, index) => (
                                        <div key={index}>{error}</div>
                                    ))}
                                </Alert>
                            </Box>
                        )}

                        {/* Display success message */}
                        {successMessage && (
                            <Box sx={{ mb: 3 }}>
                                <Alert severity="success">{successMessage}</Alert>
                            </Box>
                        )}

                        {loginMode === 'password' && renderPasswordForm()}
                        {loginMode === 'otp-email' && renderOtpEmailForm()}
                        {loginMode === 'otp-code' && renderOtpCodeForm()}

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                position: 'absolute',
                                bottom: 8,
                                left: 8,
                                fontSize: '0.7rem',
                                opacity: 0.7,
                                '&:hover': {
                                    opacity: 1
                                }
                            }}
                        >
                            <Link
                                component={RouterLink}
                                to="/credits"
                                sx={{
                                    color: theme.navigationColor,
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                Image Credits
                            </Link>
                        </Typography>
                    </Grid>

                    {/* Right side - Illustration */}
                    <Grid item xs={false} md={7} lg={8}
                        sx={{
                            backgroundImage: 'url(/images/login-illustration.jpg)',
                            backgroundRepeat: 'no-repeat',
                            backgroundColor: theme.subtleAccent,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            display: { xs: 'none', md: 'block' },
                            position: 'relative',
                            minHeight: '100vh',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: alpha(theme.navigationColor, 0.1),
                                zIndex: 0,
                            }
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: { xs: 40, md: 80 },
                                left: 40,
                                zIndex: 1,
                                maxWidth: 490,
                                color: theme.backgroundColor,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                padding: 3,
                                borderRadius: 2,
                                transform: 'translateY(-40px)',
                                '& h3, & h6': {
                                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                }
                            }}
                        >
                            <Typography variant="h3" fontWeight="700" gutterBottom>
                                Manage your schedule with ease
                            </Typography>
                            <Typography variant="h6">
                                Stay organized with MindShare, online or offline
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </LoginLayout>
    );
};
